import cryptographerService from "@/services/cryptographer-service";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { VaultState, VaultSyncState } from "../state";
import { VaultDocument } from "@syfo/types/vault.types";
import { decodeBase64 } from "../../utils/base64";
import { getVaultMetadata, getVaultBlob } from "../../utils/vault-storage";

export const readVaultThunk = createAsyncThunk<VaultState, string>(
  'vault/loadVault',
  async (id: string) => {

    const crypto = cryptographerService.getCryptographer(id);
    if (!crypto) {
      throw new Error('No cryptographer available for this vault. Please unlock the vault first.');
    }
    
    const metadata = getVaultMetadata(id);
    if (!metadata) {
      throw new Error('Vault not found in localStorage');
    }
    
    const crypt = getVaultBlob(id);
    if (!crypt) {
      throw new Error('Vault data not found in localStorage');
    }

    const docRaw = await crypto.decryptData(decodeBase64(crypt));

    const syncState: VaultSyncState = { 
      lastSyncDate: metadata.lastSyncDate || new Date(0).toISOString(), 
      lastChangeDate: metadata.modifiedAt || metadata.lastSyncDate || new Date(0).toISOString(),
      serverLastModified: metadata.serverLastModified
    };
    
    // Parse raw JSON
    const rawDoc = JSON.parse(docRaw);

    // Deserialize entire document using comprehensive VaultDocument schema
    try {
      const doc = VaultDocument.deserialize(rawDoc);

      return {
        info: {
          id: id,
          name: metadata.name
        },
        syncState: syncState,
        data: doc
      };
    } catch (error) {
      // Log detailed Zod validation errors
      if (error && typeof error === 'object' && 'issues' in error) {
        console.error('[VaultDocument Deserialization] Zod validation errors:', JSON.stringify((error as { issues: unknown[] }).issues, null, 2));
      }
      throw error;
    }
  }
);